import { PencilIcon, TrashIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import {
  Column,
  ConfirmationDialog,
  ErrorText,
  LoadingIndicator,
  Row,
  Text,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useSetTitle } from "@/hooks/useSetTitle";
import { dates } from "@/lib/dates";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS, MIXPANEL_SUBSCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";
import { CampaignPayload } from "@/types";

export const ToolsCampaignsShowPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  if (!id) {
    throw new Error("No ID found in URL for campaign show page!");
  }

  const { data: campaign, status } =
    novaClient.internal.admin.campaigns.findOne.useQuery({
      id,
    });

  const { mutate: deleteCampaign } =
    novaClient.internal.admin.campaigns.delete.useMutation({
      onSuccess: () => {
        void navigate("/tools/campaigns");
      },
    });

  useSetTitle({
    title: "Tools | Campaigns | Show",
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.CAMPAIGNS, {
      subScreenName: MIXPANEL_SUBSCREENS.SHOW,
    });
  }, []);

  const handleDelete = () => {
    deleteCampaign({ id });
  };

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle className="!text-slate-500">
          <Link className="hover:underline" to="/tools/campaigns">
            Campaigns
          </Link>{" "}
          / <span className="text-slate-700">{id}</span>
        </TopbarTitle>
        <Row className="gap-2">
          <Button
            className="rounded-full"
            disabled={
              !campaign ||
              // TODO: Bring in actual enum.
              ((campaign.campaign.status as string) !== "DRAFT" &&
                // TODO: Bring in actual enum.
                (campaign.campaign.status as string) !== "SCHEDULED")
            }
            onClick={() => {
              mixpanelUtils.trackButtonClick(
                "Delete campaign",
                MIXPANEL_SCREENS.CAMPAIGNS,
              );
              setIsDeleteDialogOpen(true);
            }}
            variant="destructive"
          >
            <TrashIcon className="h-4 w-4" />
            Delete
          </Button>
          <Button
            className="rounded-full"
            disabled={
              !campaign ||
              // TODO: Bring in actual enum.
              ((campaign.campaign.status as string) !== "DRAFT" &&
                // TODO: Bring in actual enum.
                (campaign.campaign.status as string) !== "SCHEDULED")
            }
            onClick={() => {
              mixpanelUtils.trackButtonClick(
                "Edit campaign",
                MIXPANEL_SCREENS.CAMPAIGNS,
              );
              void navigate(`/tools/campaigns/${id}/edit`);
            }}
          >
            <PencilIcon className="h-4 w-4" />
            Edit
          </Button>
        </Row>
      </TopbarContainer>
      <ConfirmationDialog
        confirmButtonText="Delete"
        confirmationButtonVariant="destructive"
        description={
          campaign
            ? `Are you sure you want to delete "${campaign.campaign.name}"? This action cannot be undone.`
            : "Are you sure you want to delete this campaign? This action cannot be undone."
        }
        onConfirm={handleDelete}
        onOpenChange={setIsDeleteDialogOpen}
        open={isDeleteDialogOpen}
        title="Delete Campaign"
      />
      <div className="flex-1 overflow-y-auto p-6">
        {status === "pending" && <LoadingIndicator variant="medium" />}
        {status === "error" && (
          <ErrorText errorMessage="Error loading campaign." />
        )}
        {status === "success" && (
          <Column className="gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-700">
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div>
                  <Text variant="small">Name</Text>
                  <p className="text-slate-700">{campaign.campaign.name}</p>
                </div>
                <div>
                  <Text variant="small">Status</Text>
                  <p className="text-slate-700">
                    {misc.toTitleCase(campaign.campaign.status)}
                  </p>
                </div>
                <div>
                  <Text variant="small">Campaign Tag</Text>
                  <p className="text-sm text-slate-700">
                    {campaign.campaignTag.name}
                  </p>
                </div>
                <div>
                  <Text variant="small">Internal Phone Number</Text>
                  <p className="text-sm text-slate-700">
                    {campaign.internalPhoneNumber.phone}
                  </p>
                </div>
                <div>
                  <Text variant="small">Sent At</Text>
                  <p className="text-slate-700">
                    {campaign.campaign.sentAt
                      ? dates.formatIsoDateTimeForUser(campaign.campaign.sentAt)
                      : "-"}
                  </p>
                </div>
                <div>
                  <Text variant="small">Created At</Text>
                  <p className="text-slate-700">
                    {dates.formatIsoDateTimeForUser(campaign.campaign.createdAt)}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-slate-700">
                  Campaign Content
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div>
                  <Text variant="small">Content</Text>
                  <p className="whitespace-pre-wrap text-slate-700">
                    {campaign.campaign.payload.content}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-slate-700">
                  Segmentation Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div>
                  <Text variant="small">Desired Positions</Text>
                  <p className="text-slate-700">
                    {/* Let's be defensive and assume could be optional for backwards compatibility. */}
                    {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                    {campaign.campaign.payload.desiredPositionSelections?.length
                      ? campaign.campaign.payload.desiredPositionSelections.join(
                          ", ",
                        )
                      : "-"}
                  </p>
                </div>
                <div>
                  <Text variant="small">Target Null Desired Position</Text>
                  <p className="text-slate-700">
                    {campaign.campaign.payload.shouldTargetNullDesiredPosition
                      ? "Yes"
                      : "No"}
                  </p>
                </div>
                <div>
                  <Text variant="small">Target Unregistered Users</Text>
                  <p className="text-slate-700">
                    {campaign.campaign.payload.shouldTargetUnregisteredUsers
                      ? "Yes"
                      : "No"}
                  </p>
                </div>
                <div>
                  <Text variant="small">Locations</Text>
                  {/* Let's be defensive and assume could be optional for backwards compatibility. */}
                  {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                  {campaign.campaign.payload.locationAndRadiusSelections
                    ?.length ? (
                    <ul className="list-disc pl-6">
                      {campaign.campaign.payload.locationAndRadiusSelections.map(
                        (
                          loc: CampaignPayload["locationAndRadiusSelections"][number],
                          index: number,
                        ) => (
                          <li key={index} className="text-slate-700">
                            {loc.location} ({loc.radius} miles)
                          </li>
                        ),
                      )}
                    </ul>
                  ) : (
                    <p className="text-slate-700">-</p>
                  )}
                </div>
                <div>
                  <Text variant="small">Campaign Tags (Include)</Text>
                  <p className="text-slate-700">
                    {/* Let's be defensive and assume could be optional for backwards compatibility. */}
                    {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                    {campaign.campaign.payload.campaignTagIncludeSelections
                      ?.length
                      ? campaign.campaign.payload.campaignTagIncludeSelections.join(
                          ", ",
                        )
                      : "-"}
                  </p>
                </div>
                <div>
                  <Text variant="small">Campaign Tags (Exclude)</Text>
                  <p className="text-slate-700">
                    {/* Let's be defensive and assume could be optional for backwards compatibility. */}
                    {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                    {campaign.campaign.payload.campaignTagExcludeSelections
                      ?.length
                      ? campaign.campaign.payload.campaignTagExcludeSelections.join(
                          ", ",
                        )
                      : "-"}
                  </p>
                </div>
                <div>
                  <Text variant="small">Phone Numbers to Include</Text>
                  <p className="text-slate-700">
                    {campaign.campaign.payload.phoneNumbersToInclude ?? "-"}
                  </p>
                </div>
                <div>
                  <Text variant="small">Phone Numbers to Exclude</Text>
                  <p className="text-slate-700">
                    {campaign.campaign.payload.phoneNumbersToExclude ?? "-"}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-700">Analysis</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div>
                  <Text variant="small">Reached Successfully</Text>
                  <p className="text-slate-700">
                    {(
                      campaign.campaign.analysis as {
                        reachedSuccessfully?: number;
                        reachedUnsuccessfully?: number;
                      } | null
                    )?.reachedSuccessfully?.toString() ?? "-"}
                  </p>
                </div>
                <div>
                  <Text variant="small">Reached Unsuccessfully</Text>
                  <p className="text-slate-700">
                    {(
                      campaign.campaign.analysis as {
                        reachedSuccessfully?: number;
                        reachedUnsuccessfully?: number;
                      } | null
                    )?.reachedUnsuccessfully?.toString() ?? "-"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Column>
        )}
      </div>
    </Column>
  );
};
